import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterSpell,
  CharacterAction,
  CharacterAbility,
  CharacterSheetInterface,
  SpellsFromList,
  Attack,
  CharacterItem,
  Ability,
  CharacterWeapon,
  StoredSpell,
  CreatureAbility,
  ACTION_TYPES,
  SKILLS,
  SpellsFromListWithCosts,
  SubscriptionType,
  CharacterFeature,
  // CharacterWeapon,
} from "@dndModels";
import { CASTING_TIME_MAP, STANDARD_CASTING_TIMES } from "@dndModels/Constants";

// import API from "../../../utilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import FloatMenu from "@components/input/menus/FloatMenu";

import CharacterSkillInput from "@dndComponents/character/skills/CharacterSkillInput";
import CharacterSettings from "@dndComponents/character/customizations/CharacterSettings";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSpellDetails from "@dndComponents/character/spells_and_abilities/CharacterSpellDetails";
import CharacterItemDetailsSidebar from "@dndComponents/character/inventory/CharacterItemDetailsSidebar";
import CharacterAttackSidebar from "@dndComponents/character/inventory/CharacterAttackSidebar";
import DisplayStringWithReplacementsRecursive from '@dndComponents/display/DisplayStringWithReplacementsRecursive';
import LoadingBox from '@components/input/LoadingBox';

interface Props {
  char_int: CharacterSheetInterface;
  char: Character;
  shared: boolean;
}

// const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterActions({
  char_int,
  char,
  shared,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [real_reloading, setRealReloading] = useState(false);
  const [reloading_costs, setReloadingCosts] = useState(false);
  const [show_hidden, setShowHidden] = useState(false);
  const [show_rituals, setShowRituals] = useState(false);
  const [show_extra_action_types, setShowExtraActionTypes] = useState(false);
  const [view, setView] = useState<"All" | "Favorites" | "Start of Turn" | "End of Turn" | "Action" | "Bonus Action" | "Reaction" | "Other">("All");
  const [sub_view, setSubView] = useState<{ [key: string]: string }>({
    "Start of Turn": "Standard",
    "End of Turn": "Standard",
    "Actions": "Standard",
    "Bonus Actions": "Standard",
    "Reactions": "Standard",
    "Other": "Standard"
  });
  const [drawer, setDrawer] = useState("");
  const [details_action, setDetailsAction] = useState<CharacterAction | CharacterFeature | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback((triggered_by: SubscriptionType, from_recalc: boolean) => {
    if (triggered_by !== "Spells Recalculated" || !from_recalc) {
      // console.log("Actions");
      setReloading(true);
    }
  }, []);

  const actionsChanged = useCallback((triggered_by: SubscriptionType, from_recalc: boolean) => {
    setRealReloading(true);
  }, []);

  useEffect(() => {
    char_int.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    char_int.subscription_holder.subscribe("Display", mounted, recalculated);
    char_int.subscription_holder.subscribe("Spells Recalculated", mounted, recalculated);
    char_int.subscription_holder.subscribe("Actions", mounted, actionsChanged);
  }, [char_int, recalculated]);
  
  useEffect(() => {
    if (real_reloading) {
      setRealReloading(false);
    }
  }, [real_reloading, setRealReloading]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  useEffect(() => {
    if (reloading_costs) {
      setReloadingCosts(false);
    }
     
  }, [reloading_costs, setReloadingCosts]);

  const validAction = (o: CharacterAction) => {
    // if (o.name.includes("Bite")) {
    //   debugger;
    // }
    if (o.action instanceof Attack) {
      return true; // !character.unarmed_strike_customizations.hidden;
    } else {
      if (o.action instanceof CharacterSpell) {
        if (o.action.hidden) {
          return false;
        } else if (!o.lowest) {
          return o.action.show_upcasts.includes(o.level);
        } else if (o.ritual_only && o.action.hide_ritual) {
          return false;
        } else if (o.action.ritual && !show_rituals) {
          return false;
        } else {
          return true;
        }
      } else if (o.action instanceof StoredSpell) {
        if (o.action.hidden) {
          return false;
        } else {
          return true;
        }
      } else if (o.action instanceof CharacterAbility) {
        if (o.action.ritual && !show_rituals) {
          return false;
        } else if (o.action.the_ability instanceof SpellsFromList || 
          o.action.the_ability instanceof SpellsFromListWithCosts) {
          if (o.special_resource) {
            return !o.action.hide_special;
          } else if (!o.lowest) {
            return o.action.show_upcasts.includes(o.level);
          } else if (o.ritual_only && o.action.hide_ritual) {
            return false;
          } else if (o.action.hidden) {
            return false;
          } else {
            return true;
          }
        } else if (o.action.the_ability instanceof Ability) {
          if (o.special_resource) {
            return !o.action.hide_special;
          } else if (o.action.hidden) {
            return false;
          } else {
            return true;
          }
        } else if (o.action.hidden) {
          return false;
        } else if (o.action.the_ability instanceof CreatureAbility) {
          return true;
        } else {
          // console.log(o);
          return true;
        }
      } else if (o.action instanceof CharacterWeapon) {
        return true;
      } else {
        // console.log(o);
        return true;
      }
    }
  }

  const renderActionGroup = (group: "Favorites" | "Start of Turn" | "End of Turn" | "Actions" | "Bonus Actions" | "Reactions" | "Other") => {
    const log_it = false; // group === "Actions"; // group === "Bonus Actions"; // 
    if (log_it) {
      console.log(char_int.actions);
      // debugger;
    }
    // let filtered = char.actions;
    let filtered = char_int.actions.filter(o => 
      // o.name.includes("Divine Smite") &&
      o.action && !o.is_passive &&
      (show_hidden || validAction(o))
    );
    // console.log(filtered);
    if (log_it) {
      // console.log(char.actions.filter(o => o.name.includes("False")));
      console.log(filtered.filter(o => o.name.includes("False")));
      // debugger;
    }
    if (!show_rituals) {
      filtered = filtered.filter(o => !o.ritual_only);
    }
    filtered = filtered.filter(o => 
      !(o.action instanceof CharacterAbility) || 
      !(o.action.the_ability instanceof Ability) ||
      o.action.the_ability.show_in.includes("Actions"));
    // filtered = filtered.filter(o => o.name.includes("Unseen"));
    if (log_it) {
      // console.log(char.actions.filter(o => o.name.includes("False")));
      console.log(filtered.filter(o => o.name.includes("False")));
      // debugger;
    }
    switch (group) {
      case "Favorites": 
        filtered = filtered.filter(o => 
          o.favorite
        );
      break;
      case "Actions": 
        filtered = filtered.filter(o => 
          !o.ritual_only && 
          ["A","Attack"].includes(o.casting_time)
        );
      break;
      case "Bonus Actions": 
        filtered = filtered.filter(o => 
          !o.ritual_only && 
          ["BA"].includes(o.casting_time)
        );
      break;
      case "Reactions": 
        filtered = filtered.filter(o => 
          !o.ritual_only && 
          ["RA"].includes(o.casting_time)
        );
      break;
      case "Other":
        // console.log(STANDARD_CASTING_TIMES);
        // console.log(filtered.map(o => o.casting_time));
        filtered = filtered.filter(o => 
          (!STANDARD_CASTING_TIMES.includes(o.casting_time) || o.ritual_only)
        );
      break;
      default: 
        const map = CASTING_TIME_MAP as any;
        // map["A"] += "s";
        // map["Attack"] += "s";
        // map["BA"] += "s";
        // map["RA"] += "s";
        filtered = filtered.filter(o => 
          !o.ritual_only && 
          map[o.casting_time] === group
        );
      break;
    }
    if (view === "All" && group !== "Favorites") {
      filtered = filtered.filter(o => !o.favorite);
    }
    if (log_it) {
      console.log(filtered.filter(o => o.name.includes("False")));
      debugger;
    }
    if (filtered.length > 0) {
      let menu: string[] = ["Standard"];
      switch(group) {
        case "Actions": 
          menu = ["Standard",
            ...char_int.full_action_action_types.filter(o => 
              o.display_specials.length > 0 && 
              !o.display_specials.includes("Description")).map(o => { return o.name; }),
            ...char_int.full_action_action_types.filter(o => 
              o.display_specials.length === 0 || 
              o.display_specials.includes("Description")).map(o => { return o.name; })];
        break;
        case "Bonus Actions": 
          menu = ["Standard",
            ...char_int.bonus_action_action_types.filter(o => 
              o.display_specials.length > 0 && 
              !o.display_specials.includes("Description")).map(o => { return o.name; }),
            ...char_int.bonus_action_action_types.filter(o => 
              o.display_specials.length === 0 || 
              o.display_specials.includes("Description")).map(o => { return o.name; })];
        break;
        case "Reactions": 
          menu = ["Standard",
            ...char_int.reaction_action_types.filter(o => 
              o.display_specials.length > 0 && 
              !o.display_specials.includes("Description")).map(o => { return o.name; }),
            ...char_int.reaction_action_types.filter(o => 
              o.display_specials.length === 0 || 
              o.display_specials.includes("Description")).map(o => { return o.name; })];
        break;
        case "Other": 
          menu = ["Standard",
            ...char_int.full_action_action_types.filter(o => 
              o.display_specials.length > 0 && 
              !o.display_specials.includes("Description")).map(o => { return o.name; }),
            ...char_int.full_action_action_types.filter(o => 
              o.display_specials.length === 0 || 
              o.display_specials.includes("Description")).map(o => { return o.name; })];
        break;
      }
      return (
        <>
          <Grid
            style={{ fontWeight: "bold", fontSize: "15px" }}
            container
            spacing={0}
            direction="row"
            size={12}>
            <Grid size={3}>
              &nbsp;{ group }
            </Grid>
            <Grid size={9}>
              { show_extra_action_types && menu.length > 1 &&
                <FloatMenu
                  options={menu}
                  selected={sub_view[group]}
                  onSelect={(view: string) => {
                    const new_sub_view = sub_view;
                    new_sub_view[group] = view;
                    setSubView(new_sub_view);
                  }}
                />
              }
            </Grid>
          </Grid>
          <Grid
            style={{ fontWeight: "bold" }}
            container
            spacing={0}
            direction="row"
            size={12}>
            <Grid size={3} className="centered">Name</Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={9}>
              <Grid
                className="centered"
                size={3}>Cost</Grid>
              <Grid size={3}
                className="centered">Range/Area</Grid>
              <Grid
                className="centered"
                size={3}>Attack/DC</Grid>
              <Grid
                className="centered"
                size={3}>Effect</Grid>
            </Grid>
          </Grid>
          { sub_view[group] === "Standard" || !show_extra_action_types ?
            renderStandardActionsForGroup(group, filtered)
          :
            renderActionsOfType(filtered, group, sub_view[group])
          }
          { group === "Other" && !show_rituals && char_int.actions.filter(o => o.ritual_only).length > 0 &&
            <Grid size={12}
              className="centered header">Rituals are Hidden</Grid>
          }
        </>
      );
    }
  }

  const renderStandardActionsForGroup = (group: string, actions: CharacterAction[]) => {
    let action_types: string[] = [];
    switch(group) {
      case "Actions": 
        action_types = char_int.full_action_action_types.filter(o => 
          o.display_specials.length > 0 && 
          !o.display_specials.includes("Description")).map(o => { return o.name; });
      break;
      case "Bonus Actions": 
        action_types = char_int.bonus_action_action_types.filter(o => 
          o.display_specials.length > 0 && 
          !o.display_specials.includes("Description")).map(o => { return o.name; });
      break;
      case "Reactions": 
        action_types = char_int.reaction_action_types.filter(o => 
          o.display_specials.length > 0 && 
          !o.display_specials.includes("Description")).map(o => { return o.name; });
      break;
      case "Other": 
        action_types = char_int.full_action_action_types.filter(o => 
          o.display_specials.length > 0 && 
          !o.display_specials.includes("Description")).map(o => { return o.name; });
      break;
      default: 
        const action_types_finder = actions.map(o => o.special_action_type);
        action_types = ACTION_TYPES.filter(o => common_util.anyOverlaps(action_types_finder, o.display_specials) && !o.display_specials.includes("Description")).map(o => o.name);
      break;
    }
    return action_types.map(type => {
      return renderActionsOfType(actions, group, type);
    });
  }

  const renderActionsOfType = (actions: CharacterAction[], group: string, type: string) => {
    const log_it = false; // group === "Actions"; // 
    if (log_it) {
      console.log(actions.filter(o => o.name.includes("Breath Weapon")));
      // actions = actions.filter(o => o.name.includes("Drink"));
      debugger;
    }
    const return_us: any[] = [];
    try {
      const action_type = ACTION_TYPES.filter(o => o.name === type)[0];
      let extra_attacks = "";
      if ((group === "Actions" || group === "Favorites") && type === "Attack" && char_int.extra_attacks > 0) {
        extra_attacks = `${char_int.extra_attacks + 1} Attacks per Action`;
        // debugger;
      }
      return_us.push(
        <Grid
          key={type}
          style={{ fontWeight: "bold" }}
          size={12}>
          &nbsp;{ type } { extra_attacks.length > 0 && <span style={{ color: "gray"}}>- { extra_attacks }</span> }
        </Grid>
      );
      if (action_type) {
        if (action_type.display_specials.length > 0) {
          if (action_type.display_specials.includes("Description")) {
            return_us.push(
              <Grid size={12} key={`${type}_desc`}>
                { action_type.description }
              </Grid>
            );
          } else {
            if (log_it) {
              console.log(actions.filter(o => o.name.includes("False")));
            }
            let dingen = action_type.name === "Attack" ? 
              actions.filter(o => 
                o.special_action_type !== "None" && 
                (action_type.display_specials.includes(o.special_action_type) || o.casting_time === "Attack")) : 
              actions.filter(o => 
                o.special_action_type !== "None" && 
                action_type.display_specials.includes(o.special_action_type) &&
                o.casting_time !== "Attack"
              );
            dingen = dingen.sort((a, b) => { 
              return a.getName(char_int).localeCompare(b.getName(char_int));
            });
            if (dingen && dingen.length > 0) {
              return_us.push(
                <Grid size={12} key={`${type}_specials`}>
                  <Grid container spacing={0} direction="row">
                    { dingen.map(action => {
                      return (
                        <Grid size={12} key={`${action.true_id}_${action.type}_${action.level}_${action.slot}`}>
                          <CharacterActionInput
                            char_int={char_int}
                            char={char}
                            action={action}
                            shared={shared}
                            level={action.level}
                            show_casting_time={group === "Favorites"}
                            onOpen={() => {
                              setDetailsAction(action);
                              setDrawer("details");
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              );
            }
          }
        } else {
          action_type.display_us.forEach(skill_id => {
            const skill = SKILLS.filter(o => o._id === skill_id)[0];
            if (skill) {
              return_us.push(
                <Grid
                  key={`${type}_${skill_id}`}
                  size={{
                    xs: 12
                  }}>
                  <CharacterSkillInput
                    char={char_int}
                    skill={skill}
                  />
                </Grid>
              );
            } 
          });
        }
      }
    } catch (ex) {
      console.error(ex);
      console.error(ACTION_TYPES);
    }
    return return_us;
  }

  const renderExtras = () => {
    const action = details_action;
    const open = drawer === "details";
    if (open) {
      if (action instanceof CharacterAction) {
        // console.log(action);
        let char_item: CharacterItem | undefined = undefined;
        if (action.action instanceof CharacterItem) {
          char_item = action.action;
        } else if (char_int instanceof Character) {
          if (action.action instanceof CharacterAbility && action.action.item_id !== "") {
            const item_id = action.action.item_id;
            char_item = char_int.inventory.items.find(o => o.true_id === item_id);
          }
        }
        return (
          <>
            <ModalOrDrawer
              open={ drawer === "details" } 
              onClose={() => {
                setDrawer("");
                char_int.subscription_holder.modalClosed();
              }}>
              { char_item && char_int instanceof Character ? 
                <CharacterItemDetailsSidebar
                  char={char_int}
                  char_item={char_item}
                  action={action}
                  shared={shared}
                  onRemove={() => {
                    char_int.changed();
                    char_int.subscription_holder.resourcesChanged();
                    char_int.subscription_holder.recalculate();
                    setDrawer("");
                  }}
                /> 
              : (action.action instanceof CharacterSpell || 
                action.action instanceof CharacterAbility) ? 
                <CharacterSpellDetails
                  char_int={char_int}
                  obj={ action.action }
                  level={ action.level }
                  type={action.type}
                  action={action}
                  shared={shared}
                  onObjChange={(obj: CharacterSpell | CharacterAbility) => {
                    if (obj instanceof CharacterSpell) {
                      const spell_finder = char_int.mind.spells.filter(o => o.true_id === obj.true_id);
                      if (spell_finder.length === 1) {
                        const char_spell = spell_finder[0];
                        char_spell.copy(obj);
                        char_int.subscription_holder.recalculate();
                        char_int.changed();
                      } else {
                        console.error("Spell Find Error:");
                        console.error(obj);
                        console.error(spell_finder);
                        console.error(char_int.mind.spells);
                      }
                    } else {
                      let ability_finder = char_int.mind.abilities.filter(o => o.true_id === obj.true_id);
                      if (ability_finder.length === 0) {
                        ability_finder = char_int.mind.spell_as_abilities.filter(o => o.true_id === obj.true_id);
                      }
                      if (ability_finder.length === 1) {
                        const char_ability = ability_finder[0];
                        char_ability.copy(obj);
                        char_int.subscription_holder.recalculate();
                        char_int.changed();
                      } else {
                        console.error("Ability Find Error:");
                        console.error(obj);
                        console.error(ability_finder);
                        console.error(char_int.mind.abilities);
                      }
                    }
                  }}
                  onClose={() => {
                    setDrawer("");
                  }}
                /> 
              : action.action instanceof Attack && 
                <CharacterAttackSidebar
                  char={char_int}
                  attack={action.action}
                />
              }
            </ModalOrDrawer>
          </>
        );
      } else if (action instanceof CharacterFeature) {
        return (
          <>
            <ModalOrDrawer
              open={ drawer === "details" } 
              onClose={() => {
                setDrawer("");
              }}>
              <Grid container spacing={0} direction="row">
                <Grid size={12} className="header">{ action.name }</Grid>
                <Grid size={12}>
                  <DisplayStringWithReplacementsRecursive cfs={char_int} display_string={action.description} />
                </Grid>
              </Grid>
            </ModalOrDrawer>
          </>
        );
      }
    }
    return null;
  }

  // console.log(char.actions);
  const view_options = ["All"];
  // ,"Action","Bonus Action","Reaction","Other"];
  const actions = char_int.actions.filter(o => !o.hidden);
  const favorites = actions.filter(o => o.favorite);
  const casting_times = actions.map(o => o.casting_time);
  if (favorites.length > 0) {
    view_options.push("Favorites");
  }
  if (casting_times.includes("Start of Turn")) {
    view_options.push("Start of Turn");
  }
  if (casting_times.includes("End of Turn")) {
    view_options.push("End of Turn");
  }
  if (casting_times.includes("A") || casting_times.includes("Attack")) {
    view_options.push("Action");
  }
  if (casting_times.includes("BA")) {
    view_options.push("Bonus Action");
  }
  if (casting_times.includes("RA")) {
    view_options.push("Reaction");
  }
  if (casting_times.includes("Special") || char_int.actions.filter(o => o.ritual_only).length > 0) {
    view_options.push("Other");
  }
  
  if (real_reloading) {
    return (<LoadingBox />);
  }
  return (
    <Grid container spacing={0} direction="row" 
      className="curved_border bg_black_transp"
      style={{
        fontSize: "12px"
      }}>
      <Grid size={3}>
      </Grid>
      <Grid size={6}>
        <div className="centered"
          style={{
            fontSize: "13px",
            fontWeight: "bold"
          }}>
          <div>
            &nbsp;Actions
          </div>
        </div>
        <CenteredMenu
          options={view_options}
          selected={view}
          onSelect={(changed: string) => {
            setView(changed as "All" | "Favorites" | "Start of Turn" | "End of Turn" | "Action" | "Bonus Action" | "Reaction" | "Other");
          }}
        />
      </Grid>
      <Grid size={3}></Grid>
      { (view === "All" || view === "Favorites") && 
        renderActionGroup("Favorites") 
      }
      { (view === "All" || view === "Start of Turn") && 
        renderActionGroup("Start of Turn") 
      }
      { (view === "All" || view === "End of Turn") && 
        renderActionGroup("End of Turn") 
      }
      { (view === "All" || view === "Action") && 
        renderActionGroup("Actions") 
      }
      { (view === "All" || view === "Bonus Action") && 
        renderActionGroup("Bonus Actions") 
      }
      { (view === "All" || view === "Reaction") && 
        renderActionGroup("Reactions") 
      }
      { (view === "All" || view === "Other") && 
        renderActionGroup("Other") 
      }
      { renderExtras() }
    </Grid>
  );
}
